/**
 * One Report an Issue form for the whole signed-in app. The life ring in the status bar
 * and the Issues page's "Report an issue" button both open this same form — a panel docked
 * in the bottom-right corner — so there is one copy of it to keep right.
 *
 * Lives in the dashboard layout and stays mounted, so a half-written report survives the
 * panel being closed and reopened (and the reporter moving between pages). It is cleared
 * only by a successful send or Discard; `inProgress` says one is waiting.
 */

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { ReportIssuePanel } from "./ReportIssuePanel";

interface ReportIssueContextValue {
  open: boolean;
  /** A report has been started and not yet sent or discarded. */
  inProgress: boolean;
  openReportIssue: () => void;
  toggleReportIssue: () => void;
}

const ReportIssueContext = createContext<ReportIssueContextValue | null>(null);

export function ReportIssueProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const openReportIssue = useCallback(() => setOpen(true), []);
  const toggleReportIssue = useCallback(() => setOpen((o) => !o), []);
  const value = useMemo(
    () => ({ open, inProgress, openReportIssue, toggleReportIssue }),
    [open, inProgress, openReportIssue, toggleReportIssue],
  );
  return (
    <ReportIssueContext.Provider value={value}>
      {children}
      <ReportIssuePanel open={open} onOpenChange={setOpen} onInProgressChange={setInProgress} />
    </ReportIssueContext.Provider>
  );
}

export function useReportIssue(): ReportIssueContextValue {
  const ctx = useContext(ReportIssueContext);
  if (!ctx) throw new Error("useReportIssue must be used inside ReportIssueProvider");
  return ctx;
}
